import { BuildingIcon, Card, GroupsIcon, ImportantIcon, SendIcon } from "@bp/ui-components";
import { type CSSProperties, type ReactNode, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { AuditActivityList } from "../../components/AuditActivityList/AuditActivityList";
import { StatusBadge } from "../../components/StatusBadge/StatusBadge";
import { useAuthClaims } from "../../hooks/useAuthClaims";
import { Page } from "../../components/Page/Page";
import {
  ATTENTION_KIND_LABEL,
  ATTENTION_KIND_TONE,
  computeAttention,
  groupAttention,
} from "../../utils/attention";
import styles from "./OverviewPage.module.scss";

// Colocated fragments — the dashboard only needs org identity + the same user
// fields the attention heuristics consume.
const OverviewPage_Organization = graphql(`
  fragment OverviewPage_Organization on Organization {
    uuid
  }
`);

const OverviewPage_User = graphql(`
  fragment OverviewPage_User on User {
    uuid
    email
    emailVerified
    emailLower
    newEmail
    impersonateOnly
    invitationOtpValidUntil
    resetPasswordOtpValidUntil
    profiles {
      uuid
      listName
      organization {
        uuid
        name
      }
    }
    profilesConnection {
      totalCount
    }
  }
`);

const OverviewPageOrganizationsQuery = graphql(`
  query OverviewPage_OrganizationsQuery {
    organizations {
      ...OverviewPage_Organization
    }
  }
`);

const OverviewPageUsersQuery = graphql(`
  query OverviewPage_UsersQuery {
    users {
      ...OverviewPage_User
    }
  }
`);

const OverviewPageProductUsageQuery = graphql(`
  query OverviewPage_ProductUsageQuery {
    planung: applications(where: { clientId: "planung-frontend-client" }) {
      clientId
      licensedOrganizationsConnection {
        totalCount
      }
    }
    bildungsplattform: applications(where: { clientId: "bp-frontend-client" }) {
      clientId
      licensedOrganizationsConnection {
        totalCount
      }
    }
  }
`);

export function OverviewPage() {
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();

  const [{ data: orgData }] = useQuery({ query: OverviewPageOrganizationsQuery });
  const [{ data: userData }] = useQuery({ query: OverviewPageUsersQuery });
  const [{ data: productData }] = useQuery({ query: OverviewPageProductUsageQuery });

  let omniUuid: string | null = null;
  try {
    omniUuid = pimAuthClaims.getOmniOrganization().uuid;
  } catch {
    omniUuid = null;
  }

  const orgs = getFragmentData(OverviewPage_Organization, orgData?.organizations ?? []).filter(
    (o) => o.uuid !== omniUuid,
  );
  const users = getFragmentData(OverviewPage_User, userData?.users ?? []);
  const problems = useMemo(() => computeAttention(users), [users]);

  const invites = users.filter((u) => u.invitationOtpValidUntil).length;

  // Per-product adoption: only the (cheap) count of organizations licensing
  // each product. Per-user/profile counts were dropped for performance.
  const productUsage = useMemo(() => {
    const rows = [
      { key: "planung", label: "TK-Planung", app: productData?.planung?.[0] },
      { key: "bp", label: "TK-Bildungsplattform", app: productData?.bildungsplattform?.[0] },
    ];
    return rows.map((r) => ({
      key: r.key,
      label: r.label,
      orgCount: r.app?.licensedOrganizationsConnection?.totalCount ?? 0,
    }));
  }, [productData]);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 11 ? "Guten Morgen" : h < 18 ? "Hallo" : "Guten Abend";
  })();
  const profile = pimAuthClaims.getProfile();
  const agentName = (
    profile.displayName || `${profile.firstName ?? ""} ${profile.lastName ?? ""}`
  ).trim();
  const firstName = agentName.split(" ")[0];

  // Only "Organisationen" has a meaningful destination; the rest are read-only
  // KPIs (there is no global people list to navigate to).
  type Stat = { label: string; value: number; icon: ReactNode; tone?: "warn"; to?: string };
  const iconStyle: CSSProperties = { width: 18, height: 18 };
  const stats: Stat[] = [
    {
      label: "Organisationen",
      value: orgs.length,
      icon: <BuildingIcon style={iconStyle} />,
      to: "/organizations",
    },
    {
      label: "Personen",
      value: users.length,
      icon: <GroupsIcon style={iconStyle} />,
    },
    {
      label: "Mit Problem",
      value: problems.length,
      icon: <ImportantIcon style={iconStyle} />,
      tone: problems.length ? "warn" : undefined,
    },
    {
      label: "Einladung offen",
      value: invites,
      icon: <SendIcon style={iconStyle} />,
    },
  ];

  return (
    <Page
      title={`${greeting}, ${firstName}`}
      description="Such oben nach Person, Schule oder Client — oder steig über die Hinweise ein."
    >
      <div className={styles.grid360}>
        <div className={styles.stack}>
          <div className={styles.statGrid}>
            {stats.map((s) => {
              const inner = (
                <>
                  <span
                    className={`${styles.statIcon} ${s.tone === "warn" ? styles.statIconWarn : ""}`}
                  >
                    {s.icon}
                  </span>
                  <span>
                    <span className={styles.statValue}>{s.value}</span>
                    <span className={styles.statLabel} style={{ display: "block" }}>
                      {s.label}
                    </span>
                  </span>
                </>
              );
              return s.to ? (
                <button
                  key={s.label}
                  type="button"
                  className={styles.statCard}
                  onClick={() => navigate(s.to as string)}
                >
                  {inner}
                </button>
              ) : (
                <div key={s.label} className={`${styles.statCard} ${styles.statCardStatic}`}>
                  {inner}
                </div>
              );
            })}
          </div>

          <Card
            header={{
              headline: "Braucht Aufmerksamkeit",
              actions:
                problems.length > 0
                  ? [
                      {
                        text: `Alle ${problems.length} ansehen`,
                        hierarchy: "tertiary",
                        callback: () => navigate("/attention"),
                      },
                    ]
                  : undefined,
            }}
            contentPadding="none"
          >
            {problems.length === 0 ? (
              <div className={styles.attnList}>
                <span className={styles.empty}>Keine Auffälligkeiten — alle Logins ok.</span>
              </div>
            ) : (
              <div className={styles.attnList}>
                {groupAttention(problems).map(({ kind, count }) => (
                  <button
                    key={kind}
                    type="button"
                    className={styles.attnRow}
                    onClick={() => navigate("/attention")}
                  >
                    <span className={styles.attnMain}>
                      <span className={styles.attnName} style={{ display: "block" }}>
                        {ATTENTION_KIND_LABEL[kind]}
                      </span>
                    </span>
                    <StatusBadge status={ATTENTION_KIND_TONE[kind]} label={String(count)} />
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className={styles.stack}>
          <Card header={{ headline: "Produkt-Nutzung" }} contentPadding="none">
            <div className={styles.prodList}>
              {productUsage.map((p) => (
                <div key={p.key} className={styles.prodRow}>
                  <span className={styles.prodName}>{p.label}</span>
                  <div className={styles.prodStats}>
                    <span className={styles.prodStat}>
                      <strong>{p.orgCount}</strong> Schulen
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card
            header={{
              headline: "Letzte Aktivität",
              actions: [
                {
                  text: "Alle",
                  hierarchy: "tertiary",
                  callback: () => navigate("/audit"),
                },
              ],
            }}
          >
            <AuditActivityList limit={6} emptyNote="Noch keine protokollierte Aktivität." />
          </Card>
        </div>
      </div>
    </Page>
  );
}
